import { yupResolver } from "@hookform/resolvers/yup";
import {
  Autocomplete,
  CircularProgress,
  Grid,
  TextField,
} from "@mui/material";
import { useSnackbar } from "notistack";
import React, { useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import FormWrapper from "src/components/FormWrapper";
import { FormProvider, RHFTextField } from "src/components/hook-form";
import { saveIncidentType, getActiveCompanies } from "src/pages/api";
import useAuth from "src/hooks/useAuth";
import * as Yup from "yup";

export default function AddIncidentCategory({ onClose, row, isEditMode }) {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "SUPER_ADMIN";

  const defaultValues = {
    name: "",
    company: null,
  };

  const schema = Yup.object().shape({
    name: Yup.string().trim().required("Incident category name is required"),
    ...(isSuperAdmin && {
      company: Yup.object().nullable().required("Company is required"),
    }),
  });

  const methods = useForm({
    resolver: yupResolver(schema),
    defaultValues,
  });

  const { handleSubmit, reset, setValue, control } = methods;

  const { data: companyOptions = [], isFetching: companiesLoading } = useQuery(
    ["companies-active-incident-category"],
    ({ signal }) => getActiveCompanies({ signal }),
    { enabled: isSuperAdmin }
  );

  useEffect(() => {
    if (isEditMode && row) {
      setValue("name", row.name || "");
      if (isSuperAdmin && row.companyId) {
        const matched = companyOptions.find((c) => String(c.id) === String(row.companyId));
        setValue("company", matched || { id: row.companyId, name: row.company?.name || row.companyName || "" });
      }
    }
  }, [isEditMode, row, setValue, isSuperAdmin, companyOptions]);

  const mutation = useMutation(
    (formData) =>
      saveIncidentType({ id: isEditMode ? row.id : undefined, req: formData }),
    {
      onSuccess: () => {
        enqueueSnackbar(
          isEditMode
            ? "Incident category updated successfully"
            : "Incident category created successfully",
          { variant: "success" }
        );
        queryClient.invalidateQueries(["incident-types"]);
        onClose();
      },
      onError: (error) => {
        const apiMessage =
          error?.response?.data?.details ||
          error?.response?.data?.error ||
          error?.response?.data?.message;
        enqueueSnackbar(
          apiMessage || error?.message || "Failed to save incident category",
          { variant: "error" }
        );
      },
    }
  );

  const onSubmit = (data) => {
    const payload = { name: data.name };
    if (isSuperAdmin && data.company) {
      payload.companyId = String(data.company.id);
    }
    mutation.mutate(payload);
  };

  return (
    <FormWrapper
      title={isEditMode ? "Edit Incident Category" : "Add Incident Category"}
      onClose={onClose}
      onSubmit={handleSubmit(onSubmit)}
      onReset={() => reset(defaultValues)}
      loading={mutation.isLoading}
      isEditMode={isEditMode}
    >
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {isSuperAdmin && (
            <Grid item xs={12}>
              <Controller
                name="company"
                control={control}
                render={({ field, fieldState }) => (
                  <Autocomplete
                    options={companyOptions}
                    value={field.value}
                    onChange={(_, val) => field.onChange(val)}
                    getOptionLabel={(opt) => opt?.name || ""}
                    isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                    loading={companiesLoading}
                    noOptionsText="No companies found"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Company"
                        required
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <>
                              {companiesLoading ? (
                                <CircularProgress color="inherit" size={20} />
                              ) : null}
                              {params.InputProps.endAdornment}
                            </>
                          ),
                        }}
                      />
                    )}
                  />
                )}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <RHFTextField
              name="name"
              required
              label="Incident Category Name"
              autoFocus
            />
          </Grid>
        </Grid>
      </FormProvider>
    </FormWrapper>
  );
}
